// ============================================================================
// API Client — eFakt Next.js Frontend
// Axios-based HTTP client for communicating with the .NET backend
// All requests are proxied via Next.js rewrites: /api/backend -> backend
// ============================================================================

import axios from "axios";
import type {
  // Entity types
  Invoice,
  InvoiceDetails,
  InvoiceHistoryEntry,
  UserAccount,
  AppSetting,
  KSeFEnvironment,
  ReceivedDocumentDetail,
  CompanySettings,
  DocumentPullSettings,
  ExternalSystemSettings,
  SessionLoopSettings,
  // API request/response DTOs
  LoginResponse,
  SetupStatusResponse,
  SetupInitializeRequest,
  SetupInitializeResponse,
  InvoiceListRequest,
  PaginatedListResponse,
  InvoiceStatusCheckResponse,
  BatchStatusCheckResponse,
  UserListRequest,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateCompanySettingsRequest,
  UpdateDocumentPullSettingsRequest,
  UpdateExternalSystemSettingsRequest,
  UpdateSessionLoopSettingsRequest,
  DocumentListRequest,
  DocumentListResponse,
  CheckNewDocumentsResponse,
  DocumentPullResult,
  SyncStatusResponse,
  ResetSyncResponse,
  FullResetResponse,
  UpdateDocumentStatusRequest,
} from "@repo/shared";

// ============================================================================
// Axios Instance
// ============================================================================

// Detect Tauri desktop environment (no Next.js rewrite proxy available)
const isTauri = typeof window !== "undefined" && "__TAURI__" in window;

function getTauriApiUrl(): string {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem("efakt-api-url");
    if (saved) return saved;
  }
  // No hardcoded fallback — login page sets the URL before any API calls
  return "/api/backend";
}

// ============================================================================
// Tauri Adapter — routes ALL axios requests through tauri-plugin-http
// which makes HTTP requests from Rust, completely bypassing browser CORS.
// ============================================================================

// Cache the imported module so we don't re-import on every request
let tauriFetchFn: typeof globalThis.fetch | null = null;

async function tauriAdapter(config: import("axios").InternalAxiosRequestConfig): Promise<import("axios").AxiosResponse> {
  if (!tauriFetchFn) {
    const mod = await import("@tauri-apps/plugin-http");
    tauriFetchFn = mod.fetch as typeof globalThis.fetch;
  }

  // Build full URL
  let url = config.url || "";
  if (config.baseURL && !url.startsWith("http")) {
    url = `${config.baseURL.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
  }

  // Append query params
  if (config.params) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(config.params)) {
      if (value !== undefined && value !== null) params.append(key, String(value));
    }
    const qs = params.toString();
    if (qs) url += (url.includes("?") ? "&" : "?") + qs;
  }

  // Build headers
  const headers: Record<string, string> = {};
  if (config.headers) {
    const raw = typeof config.headers.toJSON === "function" ? config.headers.toJSON() : config.headers;
    for (const [key, value] of Object.entries(raw)) {
      if (value !== undefined && value !== null) headers[key] = String(value);
    }
  }

  const response = await tauriFetchFn(url, {
    method: (config.method || "GET").toUpperCase(),
    headers,
    body: config.data != null
      ? (typeof config.data === "string" ? config.data : JSON.stringify(config.data))
      : undefined,
  });

  // Parse response — respect axios responseType for binary data
  const contentType = response.headers.get("content-type") || "";
  let data;
  if (config.responseType === "blob") {
    data = await response.blob();
  } else if (config.responseType === "arraybuffer") {
    data = await response.arrayBuffer();
  } else if (contentType.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  const responseHeaders: Record<string, string> = {};
  response.headers.forEach((value: string, key: string) => {
    responseHeaders[key] = value;
  });

  // Axios expects errors for non-2xx to be thrown (matching default validateStatus)
  if (response.status >= 200 && response.status < 300) {
    return { data, status: response.status, statusText: response.statusText, headers: responseHeaders, config };
  }

  // Build an error that axios interceptors can handle
  const error = new axios.AxiosError(
    `Request failed with status code ${response.status}`,
    axios.AxiosError.ERR_BAD_RESPONSE,
    config,
    null,
    { data, status: response.status, statusText: response.statusText, headers: responseHeaders, config } as import("axios").AxiosResponse,
  );
  throw error;
}

const api = axios.create({
  baseURL: isTauri ? getTauriApiUrl() : "/api/backend",
  headers: {
    "Content-Type": "application/json",
  },
  // In Tauri, use custom adapter that routes through Rust HTTP (bypasses CORS)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...(isTauri ? { adapter: tauriAdapter as any } : {}),
});

// Allow updating the base URL at runtime (called after server selection in Tauri)
export function setApiBaseUrl(url: string) {
  api.defaults.baseURL = url;
}

// ============================================================================
// Request Interceptor — attach JWT from Zustand persisted auth store
// ============================================================================

api.interceptors.request.use((config) => {
  // The Zustand auth store persists under localStorage key "efakt-auth".
  // The persisted shape is: { state: { user, token, isAuthenticated }, version: 0 }
  try {
    const raw = localStorage.getItem("efakt-auth");
    if (raw) {
      const persisted = JSON.parse(raw);
      const token = persisted?.state?.token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch {
    // Silently ignore parse errors — no token will be attached
  }
  return config;
});

// ============================================================================
// Response Interceptor — handle 401 by clearing auth and redirecting to login
// ============================================================================

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      typeof window !== "undefined" &&
      !window.location.pathname.includes("/login") &&
      !window.location.pathname.includes("/setup")
    ) {
      // Clear persisted auth state
      localStorage.removeItem("efakt-auth");
      // Redirect to login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ============================================================================
// Setup API
// ============================================================================

export const setupAPI = {
  /** GET /setup/status — check whether initial setup is required */
  getSetupStatus: async (): Promise<SetupStatusResponse> => {
    const response = await api.get<SetupStatusResponse>("/setup/status");
    return response.data;
  },

  /** POST /setup/initialize — perform initial app setup (create admin user, etc.) */
  initialize: async (data: SetupInitializeRequest): Promise<SetupInitializeResponse> => {
    const response = await api.post<SetupInitializeResponse>("/setup/initialize", data);
    return response.data;
  },
};

// ============================================================================
// Auth API
// ============================================================================

export const authAPI = {
  /** POST /auth/login — authenticate and receive JWT */
  login: async (username: string, password: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>("/auth/login", {
      username,
      password,
    });
    return response.data;
  },

  /** GET /ping — health check / session validation */
  ping: async (): Promise<void> => {
    await api.get("/ping");
  },
};

// ============================================================================
// Invoice API
// ============================================================================

export const invoiceAPI = {
  /** POST /invoice/add — create a new invoice */
  add: async (invoice: Record<string, unknown>): Promise<Invoice> => {
    const response = await api.post<Invoice>("/invoice/add", invoice);
    return response.data;
  },

  /** DELETE /invoice/del — delete invoice(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/invoice/del", { params });
  },

  /** POST /invoice/list — list invoices with optional filters (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<Invoice>> => {
    const response = await api.post<PaginatedListResponse<Invoice & { foreignCurrencyInvoice?: { currency?: string } }>>("/invoice/list", params);
    return {
      ...response.data,
      items: response.data.items.map(({ foreignCurrencyInvoice, ...inv }) => ({
        ...inv,
        currency: foreignCurrencyInvoice?.currency ?? inv.currency,
      })),
    };
  },

  /** GET /invoice/:id/details — full invoice details */
  getDetails: async (id: number): Promise<InvoiceDetails> => {
    const response = await api.get<InvoiceDetails & { foreignCurrencyInvoice?: { currency?: string } }>(`/invoice/${id}/details`);
    const { foreignCurrencyInvoice, ...rest } = response.data;
    return { ...rest, currency: foreignCurrencyInvoice?.currency ?? rest.currency };
  },

  /** GET /invoice/:id/original-data — original pre-anonymization data */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOriginalData: async (id: number): Promise<any> => {
    const response = await api.get(`/invoice/${id}/original-data`);
    return response.data;
  },

  /** GET /invoice/:id/history — audit log for an invoice */
  getHistory: async (id: number): Promise<InvoiceHistoryEntry[]> => {
    const response = await api.get<InvoiceHistoryEntry[]>(`/invoice/${id}/history`);
    return response.data;
  },

  /** POST /invoice/:id/check-ksef-status — check KSeF registration status for a single invoice */
  checkKSeFStatus: async (id: number): Promise<InvoiceStatusCheckResponse> => {
    const response = await api.post<InvoiceStatusCheckResponse>(
      `/invoice/${id}/check-ksef-status`,
    );
    return response.data;
  },

  /** POST /ksef/invoice/register — register invoice with KSeF */
  register: async (params: { id: number }): Promise<{ success: boolean; kSeFReferenceNumber?: string; errorMessage?: string }> => {
    const response = await api.post("/ksef/invoice/register", null, {
      params,
    });
    return response.data;
  },

  /** POST /ksef/invoice/:id/recheck — force re-send to KSeF to recover confirmation data (handles 440 duplicates) */
  recheckFromKSeF: async (id: number): Promise<{ registrations: Array<{ invoiceId: number; success: boolean; finalKSeFNumber?: string; confirmed: boolean; duplicateRecovered?: boolean; errorMessage?: string }> }> => {
    const response = await api.post(`/ksef/invoice/${id}/recheck`);
    return response.data;
  },

  /** POST /ksef/check-pending-statuses — batch check all pending KSeF registrations */
  checkPendingStatuses: async (): Promise<BatchStatusCheckResponse> => {
    const response = await api.post<BatchStatusCheckResponse>(
      "/ksef/check-pending-statuses",
    );
    return response.data;
  },

  /** POST /ksef/upo/fetch/:registrationId — fetch UPO from KSeF for a confirmed invoice */
  fetchUpo: async (registrationId: number): Promise<{ upoXml?: string; source: string; ksefNumber?: string; message?: string }> => {
    const response = await api.post(`/ksef/upo/fetch/${registrationId}`);
    return response.data;
  },

  /** GET /invoice/:id/visualization — open invoice FA(3) visualization as HTML (for PDF print) */
  openVisualization: async (invoiceId: number): Promise<void> => {
    const response = await api.get(`/invoice/${invoiceId}/visualization`, { responseType: "text" });
    const { openHtmlVisualization } = await import("./tauri-files");
    await openHtmlVisualization(response.data as string);
  },

  /** GET /invoice/:id/upo/visualization — open UPO visualization as HTML (for PDF print) */
  openUpoVisualization: async (invoiceId: number): Promise<void> => {
    const response = await api.get(`/invoice/${invoiceId}/upo/visualization`, { responseType: "text" });
    const { openHtmlVisualization } = await import("./tauri-files");
    await openHtmlVisualization(response.data as string);
  },
};

// ============================================================================
// User Management API
// ============================================================================

export const userAPI = {
  /** POST /users/list — list users with optional filters */
  list: async (params: UserListRequest = {}): Promise<UserAccount[]> => {
    const response = await api.post<UserAccount[]>("/users/list", params);
    return response.data;
  },

  /** GET /users/:id — get single user by ID */
  getById: async (id: number): Promise<UserAccount> => {
    const response = await api.get<UserAccount>(`/users/${id}`);
    return response.data;
  },

  /** POST /users — create a new user account */
  create: async (data: CreateUserRequest): Promise<{ id: number }> => {
    const response = await api.post<{ id: number }>("/users", data);
    return response.data;
  },

  /** PUT /users/:id — update an existing user */
  update: async (id: number, data: UpdateUserRequest): Promise<UserAccount> => {
    const response = await api.put<UserAccount>(`/users/${id}`, data);
    return response.data;
  },

  /** DELETE /users/:id — delete a user */
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/users/${id}`);
    return response.data;
  },

  /** POST /users/:id/resend-activation — resend activation email */
  resendActivation: async (id: number): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/users/${id}/resend-activation`);
    return response.data;
  },

  /** POST /users/:id/admin-reset-password — admin-initiated password reset */
  adminResetPassword: async (id: number): Promise<{ message: string; emailError?: string }> => {
    const response = await api.post<{ message: string; emailError?: string }>(`/users/${id}/admin-reset-password`);
    return response.data;
  },

  /** GET /users/:id/ksef-certificates — get all certs for a user */
  getCertificates: async (userId: number) => {
    const response = await api.get(`/users/${userId}/ksef-certificates`);
    return response.data as Array<{
      id: number;
      environment: string;
      nip: string | null;
      thumbprint: string | null;
      subject: string | null;
      expiresAt: string | null;
      uploadedAt: string;
      isValid: boolean;
    }>;
  },

  /** GET /users/:id/ksef-certificate/:env — get cert metadata for user+env */
  getCertificate: async (userId: number, environment: string) => {
    const response = await api.get(`/users/${userId}/ksef-certificate/${environment}`);
    return response.data as {
      exists: boolean;
      environment: string;
      userId: number;
      thumbprint?: string;
      subject?: string;
      expiresAt?: string;
      uploadedAt?: string;
      isValid?: boolean;
    };
  },

  /** GET /users/:id/api-keys — list API keys */
  getApiKeys: async (userId: number) => {
    const response = await api.get(`/users/${userId}/api-keys`);
    return response.data as Array<{
      id: number; name: string; keyPrefix: string | null;
      createdAt: string; lastUsedAt: string | null; expiresAt: string | null;
      isActive: boolean; revokedAt: string | null;
    }>;
  },

  /** POST /users/:id/api-keys — create API key (returns full key once) */
  createApiKey: async (userId: number, name: string, expiresInDays?: number) => {
    const response = await api.post(`/users/${userId}/api-keys`, { name, expiresInDays });
    return response.data as {
      id: number; name: string; key: string; keyPrefix: string;
      createdAt: string; expiresAt: string | null; warning: string;
    };
  },

  /** DELETE /users/:id/api-keys/:keyId — revoke API key */
  revokeApiKey: async (userId: number, keyId: number) => {
    const response = await api.delete(`/users/${userId}/api-keys/${keyId}`);
    return response.data;
  },

  /** GET /users/:userId/api-keys/:keyId/reveal — Super-admin only: get full key */
  revealApiKey: async (userId: number, keyId: number): Promise<{ id: number; name: string; key: string; keyPrefix: string }> => {
    const response = await api.get(`/users/${userId}/api-keys/${keyId}/reveal`);
    return response.data;
  },

  /** PUT /users/:id/ksef-credential-nip/:env — update NIP for user+env */
  updateCredentialNip: async (userId: number, environment: string, nip: string) => {
    const response = await api.put(`/users/${userId}/ksef-credential-nip/${environment}`, { nip });
    return response.data;
  },

  /** POST /users/:id/ksef-certificate/:env — upload cert (self only) */
  uploadCertificate: async (userId: number, environment: string, certificate: File, privateKey: File, keyPassword?: string, nip?: string) => {
    const formData = new FormData();
    formData.append("certificate", certificate);
    formData.append("privateKey", privateKey);
    if (keyPassword) formData.append("keyPassword", keyPassword);
    if (nip) formData.append("nip", nip);
    const response = await api.post(`/users/${userId}/ksef-certificate/${environment}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  /** DELETE /users/:id/ksef-certificate/:env — delete cert */
  deleteCertificate: async (userId: number, environment: string) => {
    const response = await api.delete(`/users/${userId}/ksef-certificate/${environment}`);
    return response.data;
  },
};

// ============================================================================
// Account Activation & Password Reset API (public — no auth)
// ============================================================================

export const accountAPI = {
  /** POST /auth/login — login (used internally after activation) */
  login: async (username: string, password: string): Promise<{ success: boolean; token?: string; userId?: number }> => {
    const response = await api.post("/auth/login", { username, password });
    return response.data;
  },

  /** POST /activate — activate account with token + set password */
  activate: async (token: string, password: string): Promise<{ message: string; username: string }> => {
    const response = await api.post<{ message: string; username: string }>("/activate", { token, password });
    return response.data;
  },

  /** POST /forgot-password — request password reset email */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>("/forgot-password", { email });
    return response.data;
  },

  /** POST /reset-password — reset password with token */
  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>("/reset-password", { token, password });
    return response.data;
  },
};

// ============================================================================
// Certificate Verification API
// ============================================================================

export interface CertVerifyResult {
  valid: boolean;
  thumbprint?: string;
  subject?: string;
  expiresAt?: string;
  isExpired?: boolean;
  nip?: string;
  error?: string;
}

export const certificateAPI = {
  /** POST /ksef-certificate/verify — validate cert+key+password without saving */
  verify: async (certificate: File, privateKey: File, keyPassword?: string, environment?: string): Promise<CertVerifyResult> => {
    const formData = new FormData();
    formData.append("certificate", certificate);
    formData.append("privateKey", privateKey);
    if (keyPassword) formData.append("keyPassword", keyPassword);
    if (environment) formData.append("environment", environment);
    const response = await api.post<CertVerifyResult>("/ksef-certificate/verify", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
};

// ============================================================================
// KSeF Statistics API
// ============================================================================

export const statisticsAPI = {
  /** GET /ksef/statistics — get API call logs, session logs, budget status */
  getStatistics: async () => {
    const response = await api.get("/ksef/statistics");
    return response.data;
  },

  /** GET /ksef/statistics/history — budget snapshots for charting */
  getHistory: async (period: "1h" | "1d" | "1m" = "1h") => {
    const response = await api.get(`/ksef/statistics/history?period=${period}`);
    return response.data;
  },

  /** GET /ksef/statistics/poll — lightweight change detection */
  poll: async () => {
    const response = await api.get("/ksef/statistics/poll");
    return response.data;
  },
};

// ============================================================================
// Sales Statistics API
// ============================================================================

export interface SalesStatisticsParams {
  dateFrom?: string;
  dateTo?: string;
  documentTypes?: string;
  buyerNip?: string;
  granularity?: "daily" | "weekly" | "monthly";
}

export interface SalesStatsTotals {
  totalNet: number;
  totalVat: number;
  totalGross: number;
  count: number;
  avgValue: number;
}

export interface SalesStatsByCurrency {
  currency: string;
  count: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
  plnTotalNet: number;
  plnTotalVat: number;
  plnTotalGross: number;
}

export interface SalesStatsByDocType {
  type: string;
  count: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
  plnTotalNet: number;
  plnTotalVat: number;
  plnTotalGross: number;
}

export interface SalesStatsByPeriod {
  period: string;
  count: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
}

export interface SalesStatsByVatRate {
  rate: string;
  count: number;
  totalNet: number;
  totalVat: number;
}

export interface SalesStatsTopBuyer {
  buyerNip: string;
  buyerName: string;
  count: number;
  totalGross: number;
}

export interface SalesStatsCorrectionsSummary {
  count: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
}

export interface SalesStatsKsefStatus {
  registered: number;
  pending: number;
  failed: number;
  notRegistered: number;
}

export interface SalesStatisticsResponse {
  totals: SalesStatsTotals;
  byCurrency: SalesStatsByCurrency[];
  byDocumentType: SalesStatsByDocType[];
  byPeriod: SalesStatsByPeriod[];
  byVatRate: SalesStatsByVatRate[];
  topBuyers: SalesStatsTopBuyer[];
  corrections: SalesStatsCorrectionsSummary;
  ksefStatus: SalesStatsKsefStatus;
}

export const salesStatisticsAPI = {
  /** GET /statistics/sales — sales BI with filters */
  getSalesStats: async (params?: SalesStatisticsParams): Promise<SalesStatisticsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set("dateFrom", params.dateFrom);
    if (params?.dateTo) searchParams.set("dateTo", params.dateTo);
    if (params?.documentTypes) searchParams.set("documentTypes", params.documentTypes);
    if (params?.buyerNip) searchParams.set("buyerNip", params.buyerNip);
    if (params?.granularity) searchParams.set("granularity", params.granularity);
    const qs = searchParams.toString();
    const response = await api.get<SalesStatisticsResponse>(`/statistics/sales${qs ? `?${qs}` : ""}`);
    return response.data;
  },
  /** GET /statistics/sales-report — precise verification from KSeF XML */
  getSalesReport: async (params?: SalesReportParams): Promise<SalesReportResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set("dateFrom", params.dateFrom);
    if (params?.dateTo) searchParams.set("dateTo", params.dateTo);
    if (params?.externalSystemCode) searchParams.set("externalSystemCode", params.externalSystemCode);
    if (params?.registeredByUsername) searchParams.set("registeredByUsername", params.registeredByUsername);
    if (params?.ksefStatus) searchParams.set("ksefStatus", params.ksefStatus);
    if (params?.currencyCode) searchParams.set("currency", params.currencyCode);
    const qs = searchParams.toString();
    const response = await api.get<SalesReportResponse>(`/statistics/sales-report${qs ? `?${qs}` : ""}`);
    return response.data;
  },
  /** Download sales report as CSV file */
  exportCsv: async (params?: SalesReportParams): Promise<string | undefined> => {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set("dateFrom", params.dateFrom);
    if (params?.dateTo) searchParams.set("dateTo", params.dateTo);
    if (params?.externalSystemCode) searchParams.set("externalSystemCode", params.externalSystemCode);
    if (params?.registeredByUsername) searchParams.set("registeredByUsername", params.registeredByUsername);
    if (params?.ksefStatus) searchParams.set("ksefStatus", params.ksefStatus);
    if (params?.currencyCode) searchParams.set("currency", params.currencyCode);
    const qs = searchParams.toString();
    const response = await api.get(`/statistics/sales-report/export/csv${qs ? `?${qs}` : ""}`, { responseType: "blob" });
    const contentDisposition = response.headers["content-disposition"];
    const filenameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = filenameMatch?.[1]?.replace(/['"]/g, "") || "sales-report.csv";
    const { downloadFile } = await import("./tauri-files");
    return downloadFile(response.data, filename, "text/csv; charset=utf-8");
  },
  /** Download sales report as PDF file */
  exportPdf: async (params?: SalesReportParams): Promise<string | undefined> => {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set("dateFrom", params.dateFrom);
    if (params?.dateTo) searchParams.set("dateTo", params.dateTo);
    if (params?.externalSystemCode) searchParams.set("externalSystemCode", params.externalSystemCode);
    if (params?.registeredByUsername) searchParams.set("registeredByUsername", params.registeredByUsername);
    if (params?.ksefStatus) searchParams.set("ksefStatus", params.ksefStatus);
    if (params?.currencyCode) searchParams.set("currency", params.currencyCode);
    const qs = searchParams.toString();
    const response = await api.get(`/statistics/sales-report/export/pdf${qs ? `?${qs}` : ""}`, { responseType: "blob" });
    const contentDisposition = response.headers["content-disposition"];
    const filenameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = filenameMatch?.[1]?.replace(/['"]/g, "") || "sales-report.pdf";
    const { downloadFile } = await import("./tauri-files");
    return downloadFile(response.data, filename, "application/pdf");
  },
};

// Sales Report types (KSeF XML verification)
export interface SalesReportParams {
  dateFrom?: string;
  dateTo?: string;
  externalSystemCode?: string;
  registeredByUsername?: string;
  ksefStatus?: string;
  currencyCode?: string;
}

export interface SalesReportRow {
  invoiceId: number;
  invoiceNumber: string;
  issueDate: string;
  ksefStatus: string;
  ksefNumber: string | null;
  netPln: number;
  vatPln: number;
  grossPln: number;
  netCurrency: number;
  vatCurrency: number;
  grossCurrency: number;
  currencyCode: string;
  buyerName: string;
  buyerNip: string;
  receiverName: string | null;
  receiverNipOrIdWew: string | null;
  uploadDate: string;
  ksefRegistrationDate: string | null;
  remarks: string | null;
  externalSystemCode: string | null;
  registeredByUsername: string | null;
}

export interface SalesReportCurrencySummary {
  currency: string;
  totalNet: number;
  totalVat: number;
  totalGross: number;
}

export interface SalesReportSummary {
  count: number;
  totalNetPln: number;
  totalVatPln: number;
  totalGrossPln: number;
  currencySummaries: SalesReportCurrencySummary[];
}

export interface SalesReportResponse {
  rows: SalesReportRow[];
  summary: SalesReportSummary;
  availableFilters: {
    externalSystemCodes: string[];
    registeredByUsernames: string[];
    currencies: string[];
  };
}

// ============================================================================
// Settings API
// ============================================================================

export const settingsAPI = {
  /** GET /settings/email — get email config (secrets masked) */
  getEmail: async () => {
    const response = await api.get("/settings/email");
    return response.data as {
      provider: string; fromName: string; fromAddress: string; frontendUrl: string;
      m365TenantId: string; m365ClientId: string; m365HasSecret: boolean;
      gmailUser: string; gmailHasPassword: boolean;
      smtpHost: string; smtpPort: number; smtpUser: string; smtpHasPassword: boolean; smtpSsl: boolean;
      configured: boolean;
    };
  },

  /** PUT /settings/email — save email config */
  setEmail: async (data: Record<string, unknown>) => {
    const response = await api.put("/settings/email", data);
    return response.data;
  },

  /** POST /settings/email/test — send test email */
  testEmail: async () => {
    const response = await api.post("/settings/email/test");
    return response.data;
  },

  /** GET /settings/ksef-environment — get current KSeF environment (TEST/PRD) */
  getKSeFEnvironment: async (): Promise<AppSetting> => {
    const response = await api.get<AppSetting>("/settings/ksef-environment");
    return response.data;
  },

  /** PUT /settings/ksef-environment — switch KSeF environment */
  setKSeFEnvironment: async (environment: KSeFEnvironment): Promise<AppSetting> => {
    const response = await api.put<AppSetting>("/settings/ksef-environment", {
      environment,
    });
    return response.data;
  },

  /** GET /settings/company — get company settings */
  getCompany: async (): Promise<CompanySettings> => {
    const response = await api.get<CompanySettings>("/settings/company");
    return response.data;
  },

  /** PUT /settings/company — update company settings */
  updateCompany: async (data: UpdateCompanySettingsRequest): Promise<CompanySettings> => {
    const response = await api.put<CompanySettings>("/settings/company", data);
    return response.data;
  },

  /** GET /settings/document-pull — get document pull settings */
  getDocumentPull: async (): Promise<DocumentPullSettings> => {
    const response = await api.get<DocumentPullSettings>("/settings/document-pull");
    return response.data;
  },

  /** PUT /settings/document-pull — update document pull settings */
  updateDocumentPull: async (data: UpdateDocumentPullSettingsRequest): Promise<DocumentPullSettings> => {
    const response = await api.put<DocumentPullSettings>("/settings/document-pull", data);
    return response.data;
  },

  /** GET /settings/session-loop — get session loop settings (per-environment) */
  getSessionLoop: async (): Promise<SessionLoopSettings> => {
    const response = await api.get<SessionLoopSettings>("/settings/session-loop");
    return response.data;
  },

  /** PUT /settings/session-loop — update session loop settings (per-environment) */
  updateSessionLoop: async (
    data: UpdateSessionLoopSettingsRequest,
  ): Promise<SessionLoopSettings> => {
    const response = await api.put<SessionLoopSettings>("/settings/session-loop", data);
    return response.data;
  },

  /** GET /settings/external-system — get external system integration settings */
  getExternalSystem: async (): Promise<ExternalSystemSettings> => {
    const response = await api.get<ExternalSystemSettings>("/settings/external-system");
    return response.data;
  },

  /** PUT /settings/external-system — update external system integration settings */
  updateExternalSystem: async (
    data: UpdateExternalSystemSettingsRequest,
  ): Promise<ExternalSystemSettings> => {
    const response = await api.put<ExternalSystemSettings>(
      "/settings/external-system",
      data,
    );
    return response.data;
  },

  /** POST /settings/test-webhook — test connectivity to a webhook URL from the server */
  testWebhook: async (
    url: string,
  ): Promise<{ success: boolean; statusCode: number; message: string }> => {
    const response = await api.post<{ success: boolean; statusCode: number; message: string }>(
      "/settings/test-webhook",
      { url },
    );
    return response.data;
  },

  /** GET /settings/ksef-credentials/{env} — get per-environment NIP/PESEL */
  getKSeFCredentials: async (environment: string): Promise<{ environment: string; nip: string; pesel: string; isFixed: boolean }> => {
    const response = await api.get(`/settings/ksef-credentials/${environment}`);
    return response.data;
  },

  /** PUT /settings/ksef-credentials/{env} — set per-environment company NIP */
  setKSeFCredentials: async (environment: string, nip: string): Promise<{ message: string }> => {
    const response = await api.put(`/settings/ksef-credentials/${environment}`, { nip });
    return response.data;
  },

  /** GET /settings/ksef-service-user/{env} — get designated KSeF service user for environment */
  getServiceUser: async (environment: string): Promise<{
    environment: string;
    configured: boolean;
    userId: number | null;
    username: string | null;
    hasCertificate?: boolean;
    hasCredentials?: boolean;
    isActive?: boolean;
    error?: string;
  }> => {
    const response = await api.get(`/settings/ksef-service-user/${environment}`);
    return response.data;
  },

  /** PUT /settings/ksef-service-user/{env} — set the KSeF service user for environment */
  setServiceUser: async (environment: string, userId: number): Promise<{ environment: string; userId: number; username: string; message: string; warning?: string }> => {
    const response = await api.put(`/settings/ksef-service-user/${environment}`, { userId });
    return response.data;
  },

  /** GET /settings/ksef-certificate/{env} — get certificate metadata */
  getCertificate: async (environment: string): Promise<{ exists: boolean; environment: string; thumbprint?: string; subject?: string; expiresAt?: string; uploadedAt?: string; isValid?: boolean }> => {
    const response = await api.get(`/settings/ksef-certificate/${environment}`);
    return response.data;
  },

  /** POST /settings/ksef-certificate/{env} — upload certificate + key + password */
  uploadCertificate: async (environment: string, certFile: File, keyFile: File, keyPassword?: string) => {
    const formData = new FormData();
    formData.append("certificate", certFile);
    formData.append("privateKey", keyFile);
    if (keyPassword) formData.append("keyPassword", keyPassword);
    const response = await api.post(`/settings/ksef-certificate/${environment}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  /** DELETE /settings/ksef-certificate/{env} — delete certificate */
  deleteCertificate: async (environment: string): Promise<void> => {
    await api.delete(`/settings/ksef-certificate/${environment}`);
  },

  /** GET /settings/anonymize-test-data — get anonymization setting */
  getAnonymizeTestData: async (): Promise<{ enabled: boolean; environment: string; updatedAt?: string }> => {
    const response = await api.get<{ enabled: boolean; environment: string; updatedAt?: string }>("/settings/anonymize-test-data");
    return response.data;
  },

  /** PUT /settings/anonymize-test-data — update anonymization setting */
  updateAnonymizeTestData: async (enabled: boolean): Promise<{ enabled: boolean; message: string }> => {
    const response = await api.put<{ enabled: boolean; message: string }>("/settings/anonymize-test-data", { enabled });
    return response.data;
  },

  /** GET /settings/anonymize-description-lines — get description lines setting */
  getAnonymizeDescriptionLines: async (): Promise<{ linesToKeep: number; environment: string; updatedAt?: string }> => {
    const response = await api.get<{ linesToKeep: number; environment: string; updatedAt?: string }>("/settings/anonymize-description-lines");
    return response.data;
  },

  /** PUT /settings/anonymize-description-lines — update description lines setting */
  updateAnonymizeDescriptionLines: async (linesToKeep: number): Promise<{ linesToKeep: number; message: string }> => {
    const response = await api.put<{ linesToKeep: number; message: string }>("/settings/anonymize-description-lines", { linesToKeep });
    return response.data;
  },

  // --- Data Management ---

  /** GET /settings/data-management/counts — get document counts per type */
  getDocumentCounts: async (): Promise<Record<string, number>> => {
    const response = await api.get<Record<string, number>>("/settings/data-management/counts");
    return response.data;
  },

  /** DELETE /settings/data-management/purge/{documentType} — purge all documents of a specific sale type */
  purgeSaleDocuments: async (documentType: string): Promise<{ message: string; deletedCount: number }> => {
    const response = await api.delete<{ message: string; deletedCount: number }>(`/settings/data-management/purge/${documentType}`);
    return response.data;
  },

  /** DELETE /settings/data-management/purge-received — purge all received documents */
  purgeReceivedDocuments: async (): Promise<{ message: string; deletedCount: number }> => {
    const response = await api.delete<{ message: string; deletedCount: number }>("/settings/data-management/purge-received");
    return response.data;
  },

};

// ============================================================================
// Received Documents API
// ============================================================================

export const receivedDocumentsAPI = {
  /** POST /documents/pull — trigger a manual document pull from KSeF */
  pull: async (): Promise<DocumentPullResult> => {
    const response = await api.post<DocumentPullResult>("/documents/pull");
    return response.data;
  },

  /** GET /documents/sync-status — get current sync status */
  getSyncStatus: async (): Promise<SyncStatusResponse> => {
    const response = await api.get<SyncStatusResponse>("/documents/sync-status");
    return response.data;
  },

  /** POST /documents/reset-sync — reset sync timestamp (re-pull from beginning) */
  resetSync: async (): Promise<ResetSyncResponse> => {
    const response = await api.post<ResetSyncResponse>("/documents/reset-sync");
    return response.data;
  },

  /** POST /documents/full-reset — delete all pulled documents and reset sync */
  fullReset: async (): Promise<FullResetResponse> => {
    const response = await api.post<FullResetResponse>("/documents/full-reset");
    return response.data;
  },

  /** POST /documents/list — list received documents with filters and pagination */
  list: async (params: DocumentListRequest = {}): Promise<DocumentListResponse> => {
    const response = await api.post<DocumentListResponse>("/documents/list", params);
    return response.data;
  },

  /** GET /documents/:id — get full detail for a received document */
  getById: async (id: number): Promise<ReceivedDocumentDetail> => {
    const response = await api.get<ReceivedDocumentDetail>(`/documents/${id}`);
    return response.data;
  },

  /** GET /documents/:id/xml — get raw XML content of a received document */
  getXml: async (id: number): Promise<string> => {
    const response = await api.get<string>(`/documents/${id}/xml`);
    return response.data;
  },

  /** GET /documents/:id/visualization — open received document as printable HTML (for PDF) */
  openVisualization: async (id: number): Promise<void> => {
    const response = await api.get(`/documents/${id}/visualization`, { responseType: "text" });
    const { openHtmlVisualization } = await import("./tauri-files");
    await openHtmlVisualization(response.data as string);
  },

  /** GET /documents/:id/xml/download — download XML as a file */
  downloadXml: async (id: number): Promise<void> => {
    const response = await api.get(`/documents/${id}/xml/download`, {
      responseType: "blob",
    });

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers["content-disposition"];
    let filename = `document-${id}.xml`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    const { downloadFile } = await import("./tauri-files");
    await downloadFile(response.data, filename, "application/xml");
  },

  /** PUT /documents/:id/status — update document processing status */
  updateStatus: async (id: number, data: UpdateDocumentStatusRequest): Promise<void> => {
    await api.put(`/documents/${id}/status`, data);
  },

  /** GET /documents/check-new?since=... — check for new documents since a timestamp */
  checkNew: async (since?: string): Promise<CheckNewDocumentsResponse> => {
    const params = since ? { since } : {};
    const response = await api.get<CheckNewDocumentsResponse>("/documents/check-new", {
      params,
    });
    return response.data;
  },

  /** POST /documents/export — export documents matching criteria */
  export: async (params: DocumentListRequest): Promise<{ success: boolean; exportedCount: number }> => {
    const response = await api.post<{ success: boolean; exportedCount: number }>(
      "/documents/export",
      params,
    );
    return response.data;
  },

  /** POST /documents/notify-external — notify external system about all pending documents */
  notifyExternal: async (): Promise<{ success: boolean; notifiedCount: number }> => {
    const response = await api.post<{ success: boolean; notifiedCount: number }>(
      "/documents/notify-external",
    );
    return response.data;
  },

  /** POST /documents/:id/notify-external — notify external system about a specific document */
  notifyExternalById: async (
    id: number,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(
      `/documents/${id}/notify-external`,
    );
    return response.data;
  },
};

// ============================================================================
// Correction Invoice API
// ============================================================================

export const correctionAPI = {
  /** POST /correction/list — list correction invoices (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<Record<string, unknown>>> => {
    const response = await api.post<PaginatedListResponse<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>>("/correction/list", params);
    return {
      ...response.data,
      items: response.data.items.map(({ foreignCurrencyInvoice, ...inv }) => ({
        ...inv,
        currency: foreignCurrencyInvoice?.currency ?? inv.currency,
      })),
    };
  },

  /** GET /correction/:id/details — get correction invoice details */
  getDetails: async (id: number) => {
    const response = await api.get<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>(`/correction/${id}/details`);
    const { foreignCurrencyInvoice, ...rest } = response.data;
    return { ...rest, currency: foreignCurrencyInvoice?.currency ?? rest.currency };
  },

  /** DELETE /correction/del — delete correction invoice(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/invoice/del", { params });
  },

  /** POST /ksef/invoice/register?id=:id — register correction with KSeF (uses same endpoint as invoices) */
  submitToKSeF: async (id: number) => {
    const response = await api.post("/ksef/invoice/register", null, {
      params: { id },
    });
    return response.data;
  },

  /** POST /invoice/:id/check-ksef-status — check KSeF status (uses same endpoint as invoices) */
  checkKSeFStatus: async (id: number) => {
    const response = await api.post(`/invoice/${id}/check-ksef-status`);
    return response.data;
  },
};

// ============================================================================
// Fiscal Invoice API (Faktury Fiskalne — FF)
// ============================================================================

export const fiscalInvoiceAPI = {
  /** POST /fiscalinvoice/list — list fiscal invoices (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<unknown>> => {
    const response = await api.post("/fiscalinvoice/list", params);
    return response.data as PaginatedListResponse<unknown>;
  },

  /** GET /fiscalinvoice/:id/details — get fiscal invoice details */
  getDetails: async (id: number): Promise<unknown> => {
    const response = await api.get(`/fiscalinvoice/${id}/details`);
    return response.data;
  },

  /** DELETE /fiscalinvoice/del — delete fiscal invoice(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/fiscalinvoice/del", { params });
  },

  /** POST /ksef/ff/register?id=:id — register fiscal invoice with KSeF */
  register: async (params: { id: number }) => {
    const response = await api.post("/ksef/ff/register", null, { params });
    return response.data as { success: boolean; kSeFReferenceNumber?: string; errorMessage?: string };
  },

  /** GET /ksef/ff/:id/status — check KSeF registration status */
  checkKSeFStatus: async (id: number) => {
    const response = await api.get(`/ksef/ff/${id}/status`);
    return response.data as InvoiceStatusCheckResponse;
  },

  /** GET /ksef/ff/:id/upo — fetch UPO from KSeF */
  fetchUpo: async (id: number) => {
    const response = await api.get(`/ksef/ff/${id}/upo`);
    return response.data as { upoXml?: string; source: string; ksefNumber?: string; message?: string };
  },
};

// ============================================================================
// Daily Retail Sale API (Sprzedaż Dzienna — SP)
// ============================================================================

export const retailSaleAPI = {
  /** POST /retailsale/list — list daily retail sale documents (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<unknown>> => {
    const response = await api.post("/retailsale/list", params);
    return response.data as PaginatedListResponse<unknown>;
  },

  /** GET /retailsale/:id/details — get retail sale document details */
  getDetails: async (id: number): Promise<unknown> => {
    const response = await api.get(`/retailsale/${id}/details`);
    return response.data;
  },

  /** DELETE /retailsale/del — delete retail sale document(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/retailsale/del", { params });
  },

  /** POST /ksef/sp/register?id=:id — register retail sale document with KSeF */
  register: async (params: { id: number }) => {
    const response = await api.post("/ksef/sp/register", null, { params });
    return response.data as { success: boolean; kSeFReferenceNumber?: string; errorMessage?: string };
  },

  /** GET /ksef/sp/:id/status — check KSeF registration status */
  checkKSeFStatus: async (id: number) => {
    const response = await api.get(`/ksef/sp/${id}/status`);
    return response.data as InvoiceStatusCheckResponse;
  },

  /** GET /ksef/sp/:id/upo — fetch UPO from KSeF */
  fetchUpo: async (id: number) => {
    const response = await api.get(`/ksef/sp/${id}/upo`);
    return response.data as { upoXml?: string; source: string; ksefNumber?: string; message?: string };
  },
};

// ============================================================================
// Receipt API (Paragony — PA)
// ============================================================================

export const receiptAPI = {
  /** POST /receipt/list — list receipts (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<unknown>> => {
    const response = await api.post("/receipt/list", params);
    return response.data as PaginatedListResponse<unknown>;
  },

  /** GET /receipt/:id/details — get receipt details */
  getDetails: async (id: number): Promise<unknown> => {
    const response = await api.get(`/receipt/${id}/details`);
    return response.data;
  },

  /** DELETE /receipt/del — delete receipt(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/receipt/del", { params });
  },

  /** POST /ksef/pa/register?id=:id — register receipt with KSeF */
  register: async (params: { id: number }) => {
    const response = await api.post("/ksef/pa/register", null, { params });
    return response.data as { success: boolean; kSeFReferenceNumber?: string; errorMessage?: string };
  },

  /** GET /ksef/pa/:id/status — check KSeF registration status */
  checkKSeFStatus: async (id: number) => {
    const response = await api.get(`/ksef/pa/${id}/status`);
    return response.data as InvoiceStatusCheckResponse;
  },

  /** GET /ksef/pa/:id/upo — fetch UPO from KSeF */
  fetchUpo: async (id: number) => {
    const response = await api.get(`/ksef/pa/${id}/upo`);
    return response.data as { upoXml?: string; source: string; ksefNumber?: string; message?: string };
  },
};

// ============================================================================
// Export Invoice API (Faktury Eksportowe — FE)
// ============================================================================

export const exportInvoiceAPI = {
  /** POST /exportinvoice/list — list export invoices (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<Record<string, unknown>>> => {
    const response = await api.post<PaginatedListResponse<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>>("/exportinvoice/list", params);
    return {
      ...response.data,
      items: response.data.items.map(({ foreignCurrencyInvoice, ...inv }) => ({
        ...inv,
        currency: foreignCurrencyInvoice?.currency ?? inv.currency,
      })),
    };
  },

  /** GET /exportinvoice/:id/details — get export invoice details */
  getDetails: async (id: number) => {
    const response = await api.get<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>(`/exportinvoice/${id}/details`);
    const { foreignCurrencyInvoice, ...rest } = response.data;
    return { ...rest, currency: foreignCurrencyInvoice?.currency ?? rest.currency };
  },

  /** DELETE /exportinvoice/del — delete export invoice(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/exportinvoice/del", { params });
  },

  /** POST /ksef/fe/register?id=:id — register export invoice with KSeF */
  register: async (params: { id: number }) => {
    const response = await api.post("/ksef/fe/register", null, { params });
    return response.data as { success: boolean; kSeFReferenceNumber?: string; errorMessage?: string };
  },

  /** GET /ksef/fe/:id/status — check KSeF registration status */
  checkKSeFStatus: async (id: number) => {
    const response = await api.get(`/ksef/fe/${id}/status`);
    return response.data as InvoiceStatusCheckResponse;
  },

  /** GET /ksef/fe/:id/upo — fetch UPO from KSeF */
  fetchUpo: async (id: number) => {
    const response = await api.get(`/ksef/fe/${id}/upo`);
    return response.data as { upoXml?: string; source: string; ksefNumber?: string; message?: string };
  },
};

// ============================================================================
// WWD API (Wewnątrzwspólnotowa Dostawa — WD)
// ============================================================================

export const wwdAPI = {
  /** POST /wwd/list — list intra-community delivery invoices (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<Record<string, unknown>>> => {
    const response = await api.post<PaginatedListResponse<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>>("/wwd/list", params);
    return {
      ...response.data,
      items: response.data.items.map(({ foreignCurrencyInvoice, ...inv }) => ({
        ...inv,
        currency: foreignCurrencyInvoice?.currency ?? inv.currency,
      })),
    };
  },

  /** GET /wwd/:id/details — get WWD invoice details */
  getDetails: async (id: number) => {
    const response = await api.get<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>(`/wwd/${id}/details`);
    const { foreignCurrencyInvoice, ...rest } = response.data;
    return { ...rest, currency: foreignCurrencyInvoice?.currency ?? rest.currency };
  },

  /** DELETE /wwd/del — delete WWD invoice(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/wwd/del", { params });
  },

  /** POST /ksef/wd/register?id=:id — register WD invoice with KSeF */
  register: async (params: { id: number }) => {
    const response = await api.post("/ksef/wd/register", null, { params });
    return response.data as { success: boolean; kSeFReferenceNumber?: string; errorMessage?: string };
  },

  /** GET /ksef/wd/:id/status — check KSeF registration status */
  checkKSeFStatus: async (id: number) => {
    const response = await api.get(`/ksef/wd/${id}/status`);
    return response.data as InvoiceStatusCheckResponse;
  },

  /** GET /ksef/wd/:id/upo — fetch UPO from KSeF */
  fetchUpo: async (id: number) => {
    const response = await api.get(`/ksef/wd/${id}/upo`);
    return response.data as { upoXml?: string; source: string; ksefNumber?: string; message?: string };
  },
};

// ============================================================================
// Currency Invoice API (Faktury Walutowe — FW)
// ============================================================================

export const currencyInvoiceAPI = {
  /** POST /currencyinvoice/list — list currency invoices (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<Record<string, unknown>>> => {
    const response = await api.post<PaginatedListResponse<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>>("/currencyinvoice/list", params);
    return {
      ...response.data,
      items: response.data.items.map(({ foreignCurrencyInvoice, ...inv }) => ({
        ...inv,
        currency: foreignCurrencyInvoice?.currency ?? inv.currency,
      })),
    };
  },

  /** GET /currencyinvoice/:id/details — get currency invoice details */
  getDetails: async (id: number) => {
    const response = await api.get<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>(`/currencyinvoice/${id}/details`);
    const { foreignCurrencyInvoice, ...rest } = response.data;
    return { ...rest, currency: foreignCurrencyInvoice?.currency ?? rest.currency };
  },

  /** DELETE /currencyinvoice/del — delete currency invoice(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/currencyinvoice/del", { params });
  },

  /** POST /ksef/fw/register?id=:id — register currency invoice with KSeF */
  register: async (params: { id: number }) => {
    const response = await api.post("/ksef/fw/register", null, { params });
    return response.data as { success: boolean; kSeFReferenceNumber?: string; errorMessage?: string };
  },

  /** GET /ksef/fw/:id/status — check KSeF registration status */
  checkKSeFStatus: async (id: number) => {
    const response = await api.get(`/ksef/fw/${id}/status`);
    return response.data as InvoiceStatusCheckResponse;
  },

  /** GET /ksef/fw/:id/upo — fetch UPO from KSeF */
  fetchUpo: async (id: number) => {
    const response = await api.get(`/ksef/fw/${id}/upo`);
    return response.data as { upoXml?: string; source: string; ksefNumber?: string; message?: string };
  },
};

// ============================================================================
// Currency Sale Correction API (Korekty Walutowe — DK)
// ============================================================================

export const currencySaleCorrectionAPI = {
  /** POST /currencysalecorrection/list — list currency sale corrections (paginated) */
  list: async (params: InvoiceListRequest = {}): Promise<PaginatedListResponse<Record<string, unknown>>> => {
    const response = await api.post<PaginatedListResponse<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>>("/currencysalecorrection/list", params);
    return {
      ...response.data,
      items: response.data.items.map(({ foreignCurrencyInvoice, ...inv }) => ({
        ...inv,
        currency: foreignCurrencyInvoice?.currency ?? inv.currency,
      })),
    };
  },

  /** GET /currencysalecorrection/:id/details — get currency sale correction details */
  getDetails: async (id: number) => {
    const response = await api.get<Record<string, unknown> & { foreignCurrencyInvoice?: { currency?: string } }>(`/currencysalecorrection/${id}/details`);
    const { foreignCurrencyInvoice, ...rest } = response.data;
    return { ...rest, currency: foreignCurrencyInvoice?.currency ?? rest.currency };
  },

  /** DELETE /currencysalecorrection/del — delete currency sale correction(s) */
  del: async (params: { id?: number; invoiceNumber?: string }): Promise<void> => {
    await api.delete("/currencysalecorrection/del", { params });
  },

  /** POST /ksef/dk/register?id=:id — register currency correction with KSeF */
  register: async (params: { id: number }) => {
    const response = await api.post("/ksef/dk/register", null, { params });
    return response.data as { success: boolean; kSeFReferenceNumber?: string; errorMessage?: string };
  },

  /** GET /ksef/dk/:id/status — check KSeF registration status */
  checkKSeFStatus: async (id: number) => {
    const response = await api.get(`/ksef/dk/${id}/status`);
    return response.data as InvoiceStatusCheckResponse;
  },

  /** GET /ksef/dk/:id/upo — fetch UPO from KSeF */
  fetchUpo: async (id: number) => {
    const response = await api.get(`/ksef/dk/${id}/upo`);
    return response.data as { upoXml?: string; source: string; ksefNumber?: string; message?: string };
  },
};

// ============================================================================
// Dashboard API
// ============================================================================

export interface DashboardStats {
  fa: number;
  ff: number;
  fk: number;
  fe: number;
  wd: number;
  fw: number;
  dk: number;
  sp: number;
  pa: number;
}

export interface DashboardSummary {
  stats: DashboardStats;
  recent: Invoice[];
}

export const dashboardAPI = {
  /** GET /dashboard/summary — get document type stats + recent documents */
  summary: async (documentType?: string): Promise<DashboardSummary> => {
    const params = new URLSearchParams();
    if (documentType) params.set("documentType", documentType);
    params.set("limit", "20");
    const response = await api.get<DashboardSummary>(
      `/dashboard/summary${params.toString() ? `?${params}` : ""}`,
    );
    return response.data;
  },
};

// ============================================================================
// Default export — raw axios instance for advanced use cases
// ============================================================================

export default api;
